/**
 * หน้ารายละเอียดแผน MPS + ขับสถานะ + สร้างเวอร์ชันใหม่จากแผนนี้
 * (`?view=detail&mps=<mps_id>` บน `/planning/manual-plan`, `WO-P2-M5-011`, D-129 ข้อ 2-3)
 *
 * เต็มหน้าแบบ `CustomerDetailView` (D-126) โหลดจาก `GET /v1/mps-versions/{mps_id}`
 * เสมอ — ไม่ใช่จากแถวในตารางที่โหลดมาก่อนหน้านี้ ซึ่งเป็นภาพตอนโหลดรายการ ไม่ใช่
 * สถานะล่าสุด
 *
 * **ปุ่มขับสถานะทั้งสี่ (`approve`/`freeze`/`release`/`cancel`) คืนรูปเดียวกับ
 * `GET` เป๊ะ** (D-096: "The same shape answers the create, the record read and
 * all four transitions") — สำเร็จแล้วหน้าจอใช้คำตอบนั้นแทนที่ state ตรง ๆ โดยไม่ยิง
 * `GET` ซ้ำ ยกเว้น `409` (สถานะเปลี่ยนไปก่อนแล้ว) ซึ่งคำตอบนั้นไม่มีแผนมาด้วยเลย —
 * กรณีนั้นต้อง `GET` ใหม่จริง ๆ เพื่อเห็นสถานะที่เก็บอยู่จริง
 *
 * **ทุกปุ่มขับสถานะมีด่านยืนยันซ้ำในหน้า**: คลิกครั้งแรกแค่เปิดแถบ "ยืนยัน"/"ไม่ทำ"
 * แทนที่ปุ่มเดิมชั่วคราว ยังไม่ส่งคำขอใด ๆ — ไม่ใช้ `window.confirm` ซึ่งใช้ไม่ได้แน่
 * ในหลายสภาพแวดล้อม ปุ่ม "ตรึงแผน" เพิ่มช่อง `frozen_until` ก่อนแถบยืนยัน เพราะต้อง
 * เลือกวันก่อนจึงส่งได้ (D-096: `frozen_until` *คือ* การตรึง ไม่ใช่แค่เปลี่ยนสถานะ)
 *
 * 422 `APPROVER_MUST_DIFFER_FROM_CREATOR` ไม่ถูกเดาหรือซ่อนปุ่มล่วงหน้า (D-010) —
 * ปล่อยให้เซิร์ฟเวอร์ตอบแล้วแสดงข้อความของเซิร์ฟเวอร์ตามจริงพร้อมคำอธิบายสั้น
 *
 * ⚠️ ปุ่มที่แสดง/ซ่อนเป็นเพียง UX — เซิร์ฟเวอร์เป็นผู้ตัดสินทุกคำขอเสมอ `can()` อ่าน
 * จาก `permissions` ที่ได้จากเซิร์ฟเวอร์เท่านั้น ไม่เดาจาก role (D-129 ข้อ 3)
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  approveMpsVersion,
  cancelMpsVersion,
  fetchMpsVersion,
  freezeMpsVersion,
  releaseMpsVersion,
  ruleFromMpsFailure,
  type MpsFailure,
  type MpsVersionDetailResult,
} from '../../lib/mps/client';
import { PLANNING_MODULE, type MpsVersionDetail } from '../../lib/mps/types';
import {
  isFrozenUntilWithinHorizon,
  mpsDetailLinesPageCount,
  mpsDetailLinesPageSlice,
  mpsDetailLinesRowRangeLabel,
  mpsLifecycleButtons,
  mpsVersionStatusLabel,
  mpsVersionStatusTone,
  sortMpsLineDetails,
  type MpsLifecycleActionKind,
} from '../../lib/mps/mpsView';

export interface ManualPlanDetailViewProps {
  readonly mpsId: string;
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
  /** query ของรายการที่ผู้ใช้จากมา — ปุ่ม "กลับไปรายการ" คืนตัวกรองเดิมให้ครบ */
  readonly backSearch: string;
  /** query ของหน้าสร้างพร้อม `from=<mps_id>` — ปุ่ม "สร้างเวอร์ชันใหม่จากแผนนี้" */
  readonly createFromSearch: string;
  /** เรียกเมื่อขับสถานะสำเร็จ — ให้ผู้เรียก (รายการ) รู้ว่าต้องโหลดใหม่ตอนกลับไป */
  readonly onPlanChanged?: () => void;
}

const EMPTY_VALUE = '—';

type DetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly version: MpsVersionDetail }
  | MpsFailure;

/** `details.rule` เดียวที่หน้านี้ให้คำอธิบายสั้นเพิ่มนอกจากข้อความของเซิร์ฟเวอร์ (D-010) */
const APPROVER_MUST_DIFFER_FROM_CREATOR = 'APPROVER_MUST_DIFFER_FROM_CREATOR';

function failureHeading(failure: MpsFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้';
    case 'forbidden':
      return 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้';
    case 'not-found':
      return 'ไม่พบแผนนี้';
    case 'conflict':
      return 'คำขอนี้ชนกับสถานะปัจจุบัน';
    case 'rule-violation':
      return 'คำขอนี้ติดกฎทางธุรกิจ';
    case 'bad-request':
      return 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`;
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้';
    case 'invalid-response':
      return 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ';
    case 'cancelled':
      return 'ยกเลิกการเรียกข้อมูลแล้ว';
  }
}

function failureText(failure: MpsFailure): string {
  const rule = ruleFromMpsFailure(failure);
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)';
    case 'forbidden':
      return `คำขอนี้ต้องการโมดูล ${PLANNING_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่`;
    case 'not-found':
      return 'ไม่พบแผนนี้ หรืออยู่นอกขอบเขตของคุณ';
    case 'conflict':
      return 'เซิร์ฟเวอร์ตอบ 409 — มีคนเปลี่ยนสถานะของแผนนี้ไปก่อนแล้ว โหลดแผนใหม่ให้แล้ว';
    case 'rule-violation':
      return rule === null ? 'เซิร์ฟเวอร์ตอบ 422 พร้อมกฎทางธุรกิจ' : `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${rule}`;
    case 'bad-request':
      return 'ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ';
    case 'server-error':
      return 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ';
    case 'network-error':
      return failure.detail;
    case 'invalid-response':
      return 'หน้าจอปฏิเสธที่จะแสดงแผนที่อ่านได้ไม่ครบ';
    case 'cancelled':
      return 'คำขอถูกยกเลิกก่อนได้รับคำตอบ';
  }
}

function actionSuccessMessage(action: MpsLifecycleActionKind, version: MpsVersionDetail): string {
  switch (action) {
    case 'approve':
      return 'อนุมัติแล้ว';
    case 'freeze':
      return `ตรึงถึง ${version.frozen_until ?? EMPTY_VALUE} แล้ว`;
    case 'release':
      return 'ปล่อยแล้ว';
    case 'cancel':
      return 'ยกเลิกแล้ว';
  }
}

function ActionFailureNotice({ failure }: { readonly failure: MpsFailure }) {
  const rule = ruleFromMpsFailure(failure);
  const serverMessage = 'error' in failure ? failure.error.message : null;
  return (
    <div
      className="cap-notice"
      data-testid="manual-plan-detail-action-failure"
      data-failure={failure.status}
      role="status"
    >
      <StatusBadge tone="danger">ทำรายการไม่สำเร็จ</StatusBadge>
      <h3 className="cap-notice__title">{failureHeading(failure)}</h3>
      <p className="cap-notice__text">{failureText(failure)}</p>
      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="manual-plan-detail-action-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}
      {rule === APPROVER_MUST_DIFFER_FROM_CREATOR ? (
        <p className="cap-notice__note">ต้องให้พนักงานอีกคนเป็นผู้อนุมัติแผนนี้ (D-010)</p>
      ) : null}
    </div>
  );
}

export function ManualPlanDetailView({
  mpsId,
  permissions,
  apiBaseUrl,
  fetchImpl,
  backSearch,
  createFromSearch,
  onPlanChanged,
}: ManualPlanDetailViewProps) {
  const { pathname } = useLocation();
  const [state, setState] = useState<DetailState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const [confirming, setConfirming] = useState<MpsLifecycleActionKind | null>(null);
  const [frozenUntil, setFrozenUntil] = useState('');
  const [frozenUntilError, setFrozenUntilError] = useState(false);
  const [pending, setPending] = useState(false);
  const [actionFailure, setActionFailure] = useState<MpsFailure | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });

    void fetchMpsVersion(mpsId, { apiBaseUrl, fetchImpl, signal: controller.signal }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setState(result);
        return;
      }
      setState({ status: 'ready', version: result.version });
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [mpsId, apiBaseUrl, fetchImpl, reloadToken]);

  const version = state.status === 'ready' ? state.version : null;

  const startConfirm = (action: MpsLifecycleActionKind) => {
    setConfirming(action);
    setActionFailure(null);
    setSuccessMessage(null);
    setFrozenUntil('');
    setFrozenUntilError(false);
  };

  const dismissConfirm = () => {
    setConfirming(null);
    setFrozenUntilError(false);
  };

  const runAction = (action: MpsLifecycleActionKind): Promise<MpsVersionDetailResult> => {
    const options = { apiBaseUrl, fetchImpl };
    switch (action) {
      case 'approve':
        return approveMpsVersion(mpsId, options);
      case 'release':
        return releaseMpsVersion(mpsId, options);
      case 'cancel':
        return cancelMpsVersion(mpsId, options);
      case 'freeze':
        return freezeMpsVersion(mpsId, { frozen_until: frozenUntil.trim() }, options);
    }
  };

  const confirmAction = async (action: MpsLifecycleActionKind) => {
    if (pending || version === null) {
      return;
    }
    if (
      action === 'freeze' &&
      !isFrozenUntilWithinHorizon(frozenUntil, version.horizon_start, version.horizon_end)
    ) {
      setFrozenUntilError(true);
      return;
    }

    setPending(true);
    setActionFailure(null);
    const result = await runAction(action);
    setPending(false);
    if (result.status === 'cancelled') {
      return;
    }

    setConfirming(null);
    if (result.status !== 'ready') {
      setActionFailure(result);
      if (result.status === 'conflict') {
        setReloadToken((token) => token + 1);
      }
      return;
    }

    setState({ status: 'ready', version: result.version });
    setSuccessMessage(actionSuccessMessage(action, result.version));
    onPlanChanged?.();
  };

  const canCreateFromThis = can(permissions, PLANNING_MODULE, 'CREATE');

  const sortedLines = version === null ? [] : sortMpsLineDetails(version.lines);
  const totalLines = sortedLines.length;
  const pageCount = mpsDetailLinesPageCount(totalLines);
  const clampedPageIndex = Math.min(pageIndex, pageCount - 1);
  const pageLines = mpsDetailLinesPageSlice(sortedLines, clampedPageIndex);
  const rowRangeLabel = mpsDetailLinesRowRangeLabel(totalLines, clampedPageIndex);
  const visibleButtons =
    version === null
      ? []
      : mpsLifecycleButtons(version.status).filter((button) =>
          can(permissions, PLANNING_MODULE, button.requiredPermission),
        );

  return (
    <div className="cap-page" data-testid="manual-plan-detail">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="manual-plan-detail-label">
            {version === null ? 'รายละเอียดแผน' : version.version_label}
          </h1>
          <p className="cap-page__sub">รหัสภายใน {mpsId}</p>
        </div>
        <div className="cap-page__actions">
          {version === null || !canCreateFromThis ? null : (
            <Link
              className="cap-button cap-button--ghost"
              data-testid="manual-plan-detail-create-from"
              to={createFromSearch}
            >
              สร้างเวอร์ชันใหม่จากแผนนี้
            </Link>
          )}
          <Link
            className="cap-button cap-button--ghost"
            data-testid="manual-plan-detail-back"
            to={{ pathname, search: backSearch }}
          >
            กลับไปรายการ
          </Link>
        </div>
      </div>

      {successMessage === null ? null : (
        <div className="cap-notice" data-testid="manual-plan-detail-success" role="status">
          <StatusBadge tone="success">ทำสำเร็จ</StatusBadge>
          <p className="cap-notice__text">{successMessage}</p>
        </div>
      )}

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="manual-plan-detail-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายละเอียดแผน…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <div
          className="cap-notice"
          data-testid="manual-plan-detail-failure"
          data-failure={state.status}
          role="status"
        >
          <StatusBadge tone="danger">โหลดไม่สำเร็จ</StatusBadge>
          <h2 className="cap-notice__title">{failureHeading(state)}</h2>
          <p className="cap-notice__text">{failureText(state)}</p>
          <div className="cap-notice__actions">
            <Link
              className="cap-button cap-button--ghost"
              data-testid="manual-plan-detail-failure-back"
              to={{ pathname, search: backSearch }}
            >
              กลับไปรายการ
            </Link>
          </div>
        </div>
      ) : null}

      {version === null ? null : (
        <>
          <section className="cap-meta" aria-label="ข้อมูลแผน">
            <div className="cap-meta__item">
              <div className="cap-meta__label">ชื่อเวอร์ชัน</div>
              <div className="cap-meta__value cap-table__code">{version.version_label}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">โรงงาน</div>
              <div className="cap-meta__value cap-table__code">{version.plant_code}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สถานะ</div>
              <div className="cap-meta__value" data-testid="manual-plan-detail-status">
                <StatusBadge tone={mpsVersionStatusTone(version.status)}>
                  {mpsVersionStatusLabel(version.status)}
                </StatusBadge>
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ช่วงวางแผน</div>
              <div className="cap-meta__value">
                {version.horizon_start}–{version.horizon_end}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ตรึงถึง</div>
              <div className="cap-meta__value" data-testid="manual-plan-detail-frozen-until">
                {version.frozen_until ?? EMPTY_VALUE}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ผู้สร้าง</div>
              <div className="cap-meta__value cap-table__code">{version.created_by_employee_id}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ผู้อนุมัติ / เวลาอนุมัติ</div>
              <div className="cap-meta__value cap-table__code" data-testid="manual-plan-detail-approver">
                {version.approved_by_employee_id ?? EMPTY_VALUE}
                {version.approved_at === null ? '' : ` — ${version.approved_at}`}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">หมายเหตุ</div>
              <div className="cap-meta__value">{version.note ?? EMPTY_VALUE}</div>
            </div>
          </section>

          <section className="cap-card cap-card--table" aria-label="ขับสถานะแผน">
            <div className="cap-card__head">
              <h2 className="cap-card__title">
                ขับสถานะ
                <InfoHint
                  label="เกี่ยวกับการขับสถานะ"
                  text="ทุกปุ่มต้องกดยืนยันซ้ำก่อนส่งจริง และผู้อนุมัติต้องไม่ใช่ผู้สร้างแผนนี้ (D-010) ปุ่มที่ไม่แสดงอาจเป็นเพราะสิทธิ์ของคุณหรือสถานะปัจจุบันของแผนไม่เปิดให้ทำ"
                />
              </h2>
            </div>
            <div className="cap-form__actions" data-testid="manual-plan-detail-actions">
              {visibleButtons.map((button) =>
                confirming === button.action ? (
                  <div
                    key={button.action}
                    className="cap-confirm-bar"
                    data-testid={`manual-plan-detail-confirm-${button.action}`}
                  >
                    {button.action === 'freeze' ? (
                      <label className="cap-field">
                        <span className="cap-field__label">ตรึงถึงวันที่</span>
                        <input
                          className="cap-input"
                          type="date"
                          data-testid="manual-plan-detail-freeze-date"
                          value={frozenUntil}
                          min={version.horizon_start}
                          max={version.horizon_end}
                          disabled={pending}
                          onChange={(event) => {
                            setFrozenUntil(event.target.value);
                            setFrozenUntilError(false);
                          }}
                        />
                        {frozenUntilError ? (
                          <p
                            className="cap-field__hint cap-field__hint--error"
                            data-testid="manual-plan-detail-freeze-date-error"
                          >
                            เลือกวันที่ในช่วงวางแผน ({version.horizon_start}–{version.horizon_end})
                          </p>
                        ) : null}
                      </label>
                    ) : (
                      <span className="cap-confirm-bar__text">
                        ยืนยันจะ &ldquo;{button.label}&rdquo; ใช่หรือไม่?
                      </span>
                    )}
                    <button
                      type="button"
                      className="cap-button cap-button--primary"
                      data-testid={`manual-plan-detail-confirm-${button.action}-yes`}
                      disabled={pending}
                      onClick={() => {
                        void confirmAction(button.action);
                      }}
                    >
                      {pending ? 'กำลังส่ง…' : 'ยืนยัน'}
                    </button>
                    <button
                      type="button"
                      className="cap-button cap-button--ghost"
                      data-testid={`manual-plan-detail-confirm-${button.action}-no`}
                      disabled={pending}
                      onClick={dismissConfirm}
                    >
                      ไม่ทำ
                    </button>
                  </div>
                ) : (
                  <button
                    key={button.action}
                    type="button"
                    className="cap-button cap-button--primary"
                    data-testid={`manual-plan-detail-action-${button.action}`}
                    disabled={confirming !== null}
                    onClick={() => {
                      startConfirm(button.action);
                    }}
                  >
                    {button.label}
                  </button>
                ),
              )}
            </div>
            {actionFailure === null ? null : <ActionFailureNotice failure={actionFailure} />}
          </section>

          <section className="cap-card cap-card--table" aria-label="บรรทัดของแผน">
            <div className="cap-card__head">
              <h2 className="cap-card__title">บรรทัดของแผน</h2>
            </div>
            {totalLines === 0 ? (
              <div className="cap-card__foot" data-testid="manual-plan-detail-lines-empty">
                ไม่มีบรรทัด
              </div>
            ) : (
              <>
                <div className="cap-table-wrap">
                  <table className="cap-table" data-testid="manual-plan-detail-lines-table">
                    <thead>
                      <tr>
                        <th scope="col">รหัสสินค้า</th>
                        <th scope="col">วันที่</th>
                        <th scope="col">จำนวน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageLines.map((line) => (
                        <tr key={line.line_no} data-testid={`manual-plan-detail-line-${line.line_no}`}>
                          <td>
                            <code className="cap-table__code">{line.product_code}</code>
                          </td>
                          <td>{line.bucket_date}</td>
                          <td>{line.planned_qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="cap-card__foot cap-pager" data-testid="manual-plan-detail-lines-pager">
                  <span className="cap-pager__count">{rowRangeLabel}</span>
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    data-testid="manual-plan-detail-lines-prev"
                    disabled={clampedPageIndex === 0}
                    onClick={() => {
                      setPageIndex((index) => Math.max(0, index - 1));
                    }}
                  >
                    ก่อนหน้า
                  </button>
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    data-testid="manual-plan-detail-lines-next"
                    disabled={clampedPageIndex >= pageCount - 1}
                    onClick={() => {
                      setPageIndex((index) => Math.min(pageCount - 1, index + 1));
                    }}
                  >
                    ถัดไป
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="cap-card cap-card--table" aria-label="ดูเทียบผลิตจริง">
            <div className="cap-card__foot">
              <Link
                className="cap-button cap-button--ghost"
                data-testid="manual-plan-detail-compare"
                to={`/planning/production-plan?mps=${version.mps_id}`}
              >
                ดูเทียบผลิตจริง
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
